import { CodeListSchema, CodeTableSchema, type Code } from "@/types/Codes";
import { ResponseSchema } from "@/types/Response";
import { EntitySchema, type Entity } from "@/types/Entity";
import { format, formatISO } from "date-fns";
import { useAuthStore } from "@/stores/auth";
import type { ProjectInfo } from "@/types/Projects";
import type { ProjectEntity } from "@/types/ProjectEntity";
import { ProjectRole } from "@/enums/ProjectRole";
import type { GridApi, ValueFormatterParams, ValueParserParams } from "ag-grid-community";
import { ProjectStatusSchema, type ProjectStatus } from "@/types/ProjectStatus";
import { DrawBasedRetainageSchema, EntityRetainageSchema, type DrawBasedRetainage, type EntityRetainage } from "@/types/Retainage";
import z from "zod";

//TODO: Should this just be changed to return the object without dealing with this container stuff so it is more flexible?
export function parseData(schema: any, data: any, container: any): boolean {
  const resp = schema.safeParse(data);
  if (!resp.success) {
    error("Error Occurred", "API data mismatch");
    console.log(schema.safeParse(data));
    return false;
  } else {
    container.value = resp.data;
    return true;
  }
}

export function parseData2(schema: any, data: any): any {
  const resp = schema.safeParse(data);
  if (!resp.success) {
    error("Error Occurred", "API data mismatch");
    console.log(schema.safeParse(data));
    return null;
  } else {
    return resp.data;
  }
}

export const formatCurrency = (value: number | string | undefined): string => {
  if (value == null) return "$0.00";
  if (typeof value == "string") value = parseInt(value);
  let currency = value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
  return currency.startsWith("-") ? currency.replace("-", "(") + ")" : currency;
};

export const formatPercent = (value: number | string | undefined): string => {
  if (value == null) return "0%";

  return value + "%";
};

export const percentFormatter = (params: ValueFormatterParams, displayBlankBelowZero: boolean = false): string => {
  if (params.value === undefined || params.value === null) return "";

  let value = params.value;
  if (typeof value === "object") {
    value = value.value;
  }
  if (displayBlankBelowZero && value <= 0) return "";
  return value + "%";
};

export const currencyFormatter = (params: ValueFormatterParams, displayBlankZero: boolean = false): string => {
  let value = params.value;
  if (value != null && typeof value === "object") {
    value = value.value;
  }
  if (displayBlankZero && (value == null || value == 0)) return "";
  return formatCurrency(value);
};

export const dateFormatter = (params: ValueFormatterParams): string => {
  if (params.value == null || dateIsInvalid(params.value)) return "";
  return format(params.value, "MM/dd/yyyy");
};

export const percentParser = (params: ValueParserParams, digits: number = 0): number => {
  let input = Number(params.newValue);
  if (Number.isNaN(input)) return 0;
  return input < 0 ? 0 : input > 100 ? 100 : round(input, digits);
};

export const numberParser = (params: ValueParserParams, digits: number = 2, allowNegative: boolean = false): number => {
  let input = Number(params.newValue);
  if (Number.isNaN(input)) return 0;
  if (!allowNegative) input = input < 0 ? 0 : input;
  return round(input, digits);
};

export const textParser = (params: ValueParserParams): string => {
  return params.newValue == null ? "" : params.newValue;
};

export const maskProjectCode = (code: string | undefined): string => {
  if (code == null) return "";
  const cutoff = Math.min(code.length, 5);
  return code
    .split("")
    .slice(0, code.length)
    .map((v, idx) => (idx >= cutoff ? v : v === "-" ? "-" : "*"))
    .join("");
};

//TODO: Merge these
export const toFormData = (data: Object): FormData => {
  const formData = new FormData();
  for (const [key, entryVal] of Object.entries(data)) {
    let value = entryVal;
    //if (isProxy(value)) value = toRaw(value);

    if (value == null) formData.append(key, "");
    else if (value instanceof Date) formData.append(key, formatISO(value));
    else if (value instanceof Array) {
      for (let i = 0; i < value.length; i++) {
        let arrayItem = toFormData(value[i]);
        for (const [itemKey, itemValue] of arrayItem.entries()) {
          formData.append(key + "[" + i + "]." + itemKey, itemValue);
        }
      }
    } else formData.append(key, value);
  }
  return formData;
};

export const toFormData2 = (data: Object, name: string, formData: FormData | null = null): FormData => {
  if (formData == null) formData = new FormData();
  for (const [key, entryVal] of Object.entries(data)) {
    let adjustedKey = name.length > 0 ? name + "." + key : key;

    let value = entryVal;
    //if (isProxy(value)) value = toRaw(value);

    if (value == null) formData.append(adjustedKey, "");
    else if (value instanceof Date) formData.append(adjustedKey, formatISO(value));
    else if (value instanceof Array) {
      for (let i = 0; i < value.length; i++) {
        let arrayItem = toFormData(value[i]);
        for (const [itemKey, itemValue] of arrayItem.entries()) {
          formData.append(adjustedKey + "[" + i + "]." + itemKey, itemValue);
        }
      }
    } else if (value instanceof Object) {
      toFormData2(value, adjustedKey, formData);
    } else formData.append(adjustedKey, value);
  }
  return formData;
};

export const handleApiResponse = (response: any, success_handler: (data: any) => void, error_handler: (errors: ApiError[]) => void) => {
  const parse_result = ResponseSchema.safeParse(response.data);
  if (!parse_result.success) {
    error("Error Occurred", "API data mismatch");
    console.log(parse_result);
    return false;
  }
  const api_response = parse_result.data;
  if (api_response?.result) {
    success_handler(api_response?.result);
  } else if (api_response?.errors) {
    error_handler(api_response.errors);
  } else {
    success_handler(null);
  }
};

export const fileSaveAs = (url: string, fileName: string) => {
  axios.get(url, { responseType: "blob" }).then((fileResponse) => {
    const downloadLink = document.createElement("a");
    downloadLink.href = window.URL.createObjectURL(new Blob([fileResponse.data]));
    downloadLink.setAttribute("download", fileName);
    document.body.appendChild(downloadLink);
    downloadLink.click();
    downloadLink.remove();
    window.URL.revokeObjectURL(downloadLink.href);
  });
};

export const getCellEditableClass = (): string => {
  return "bg-secondary-500/20! cursor-pointer italic";
};

export const getUserGroups = async (restrict: boolean, hideAllGroup: boolean): Promise<Code[]> => {
  let codes: Code[] = [];
  await axios
    .get("/codes/user-groups?restricted=" + restrict)
    .then((response) => {
      codes = CodeListSchema.parse(response.data.result);
      if (hideAllGroup) {
        codes = codes.filter((code: Code) => code.name.toLowerCase() !== "all");
      }
    })
    .catch((e) => errorApi(e));
  return codes;
};

export const getCodeTable = async (table: number): Promise<Code[]> => {
  let codes: Code[] = [];
  await axios
    .get("/codes/" + table)
    .then((response) => {
      codes = CodeTableSchema.parse(response.data.result).codes;
    })
    .catch((e) => errorApi(e));
  return codes;
};

export const round = (amount: number, decimals: number): number => {
  return Number(amount.toFixed(decimals));
};

export const testBit = (num: number, bit: number): boolean => {
  const numAsBigInt = BigInt(num);
  const bitMask = 1n << BigInt(bit);

  return (numAsBigInt & bitMask) !== 0n;
};

// export const testBitBigint = (num: bigint, bit: number): boolean => {
//   return (num & (1n << BigInt(bit))) !== 0n;
// };

export const packBit = (num: number, value: boolean, bit: number): number => {
  let numAsBigInt: bigint = BigInt(num);
  const bitMask: bigint = 1n << BigInt(bit);

  if (value) {
    numAsBigInt = numAsBigInt | bitMask;
  } else {
    numAsBigInt = numAsBigInt & ~bitMask;
  }

  return Number(numAsBigInt);
};

// export const packBitBigInt = (num: bigint, value: boolean, bit: number): bigint => {
//   const bitMask = 1n << BigInt(bit);

//   if (value) {
//     return num | bitMask;
//   } else {
//     return num & ~bitMask;
//   }
// };

export const getEntityDisplay = (entity: Entity): string => {
  if (entity == null || entity.firstName == null) return "";
  if (entity.firstName === "TBD") return "TBD";
  return entity.firstName.length == 0 ? entity.companyName : entity.firstName + " " + entity.lastName;
};

export const getUserEntity = async (): Promise<Entity | null> => {
  let entity: Entity | null = null;
  const userInfo = useAuthStore().getUserInfo();
  await axios
    .get("/entities/" + userInfo.entity)
    .then((response) => {
      entity = EntitySchema.parse(response.data.result);
    })
    .catch((e) => errorApi(e));
  return entity;
};

export const buildUrl = (base: string, paramMap: Map<string, string | number>): string => {
  let url = base;
  let params: string[] = [];

  paramMap.forEach((value: any, key: string | number) => {
    params.push(key + "=" + value);
  });

  if (params.length) url += "?";
  url += params.join("&");
  return url;
};

export const openMap = (address: string, city: string, zip: string | null) => {
  let fullAddress;
  if (zip != null) fullAddress = encodeURIComponent(`${address},${city},${zip}`);
  else fullAddress = encodeURIComponent(`${address},${city}`);

  const url = `https://www.google.com/maps/place/${fullAddress}`;
  window.open(url);
};

export const confirmPopup = (confirm: any, headerString: string, messageString: string, acceptHandler: () => void, rejectHandler: () => void) => {
  confirm.require({
    message: messageString,
    header: headerString,
    rejectClass:
      "px-4! py-1! bg-danger-500! border-danger-500! hover:bg-danger-600! hover:border-danger-600! dark:bg-danger-400! dark:border-danger-400! dark:hover:bg-danger-300! dark:hover:border-danger-300!",
    acceptClass:
      "px-3! py-1! bg-success-500! border-success-500! hover:bg-success-600! hover:border-success-600! dark:bg-success-400! dark:border-success-400! dark:hover:bg-success-300! dark:hover:border-success-300! ",
    rejectLabel: "No",
    acceptLabel: "Yes",
    icon: "pi pi-exclamation-triangle",
    accept: acceptHandler,
    reject: rejectHandler,
    onHide: rejectHandler,
  });
};

export const dateIsInvalid = (date: Date): boolean => {
  const yearMatches = 1900 === date.getFullYear();
  const monthMatches = 0 === date.getMonth();
  const dateMatches = 1 === date.getDate();

  return yearMatches && monthMatches && dateMatches;
};

export const entityAlreadyExistsInProject = (
  project: ProjectInfo,
  sources: ProjectEntity[],
  contacts: ProjectEntity[],
  newEntity: Entity,
  projectRole?: ProjectRole
): boolean => {
  if (entityIsTBD(newEntity)) return false;

  let existingEntity = "";
  let invalid = false;
  if ((projectRole == null || projectRole !== ProjectRole.PROCESSOR) && project.processor.entityId === newEntity.entityId) {
    invalid = true;
    existingEntity = "Processor";
  } else if ((projectRole == null || projectRole !== ProjectRole.PRIMARY_LENDER) && project.lender.entityId === newEntity.entityId) {
    invalid = true;
    existingEntity = "Primary Lender";
  } else if (
    (projectRole == null || ![ProjectRole.PRIMARY_BORROWER, ProjectRole.PRIMARY_BUILDER].includes(projectRole)) &&
    project.borrower.entityId === newEntity.entityId
  ) {
    invalid = true;
    existingEntity = "Primary Borrower";
  } else if (
    (projectRole == null || ![ProjectRole.PRIMARY_BORROWER, ProjectRole.PRIMARY_BUILDER].includes(projectRole)) &&
    project.builder.entityId === newEntity.entityId
  ) {
    invalid = true;
    existingEntity = "Primary Builder";
  } else if ((projectRole == null || projectRole !== ProjectRole.PRIMARY_INSPECTOR) && project.inspector.entityId === newEntity.entityId) {
    invalid = true;
    existingEntity = "Primary Inspector";
  } else if (sources.filter((source) => !source.type.includes("PRIMARY") && source.entity.entityId === newEntity.entityId).length > 0) {
    invalid = true;
    existingEntity = "Source of Funds";
  } else if (contacts.filter((contact) => contact.entity.entityId === newEntity.entityId).length > 0) {
    invalid = true;
    existingEntity = "Contact";
  }

  if (invalid) error("Error", `Entity ${getEntityDisplay(newEntity)} already exists in the project as a ${existingEntity}`);

  return invalid;
};

export const entityIsTBD = (entity: Entity | undefined) => {
  if (entity == null) return false;
  return entity.firstName === "TBD" && entity.lastName === "TBD";
};

export const convertImportColumnToIndex = (col: Ref<string>): number => {
  return col.value === "" ? -1 : col.value.charCodeAt(0) - 65;
};

export const waitUntil = (conditionFunction: Function) => {
  const poll = (resolve: Function) => {
    if (conditionFunction()) resolve();
    else setTimeout(() => poll(resolve), 400);
  };

  return new Promise(poll);
};

export const getProjectStatuses = async (): Promise<ProjectStatus[]> => {
  let statuses: ProjectStatus[] = [];
  await axios
    .get("/project-status")
    .then((response) => {
      statuses = z.array(ProjectStatusSchema).parse(response.data.result);
    })
    .catch((e) => errorApi(e));
  return statuses;
};

export const getRetainage = async (drawId: string): Promise<EntityRetainage[]> => {
  let entityRetainageList: EntityRetainage[] = [];
  await axios
    .get("/retainage/" + drawId)
    .then((response) => {
      entityRetainageList = z.array(EntityRetainageSchema).parse(response.data.result);
    })
    .catch((e) => errorApi(e));
  return entityRetainageList;
};

export const getDrawBasedRetainage = async (drawId: string): Promise<DrawBasedRetainage> => {
  let drawBasedRetainage = DrawBasedRetainageSchema.parse({});
  await axios
    .get("/retainage/draw-based/" + drawId)
    .then((response) => {
      drawBasedRetainage = DrawBasedRetainageSchema.parse(response.data.result);
    })
    .catch((e) => errorApi(e));
  return drawBasedRetainage;
};

//TODO: This is only a temporary fix after updating ag-grid to the fact that we were doing tables the wrong way
// All tables should be using the immutableData property and just updating the ref directly instead of manually
// trying to keep them in sync
export const getTableData = (gridApi: GridApi | undefined): any[] => {
  const updatedData: any[] = [];
  gridApi?.forEachNode((node) => updatedData.push(node.data));
  return updatedData;
};

const rgbToHex = (rgb: string): string => {
  const matches = rgb.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([^,\s)]+)?\)/i);
  if (!matches) {
    return rgb;
  }

  const hex = [matches[1], matches[2], matches[3]]
    .map((x) => parseInt(x, 10))
    .map((x) => Math.max(0, Math.min(255, x)))
    .map((x) => x.toString(16).padStart(2, "0"))
    .join("");

  return `#${hex}`;
};

export const getCssColorVariable = (varName: string, format: "rgb" | "hex" = "hex"): string => {
  if (typeof window === "undefined") return "";

  const rootStyle = getComputedStyle(document.documentElement);
  let value = rootStyle.getPropertyValue(varName).trim();

  if (value.includes(",")) {
    value = `rgb(${value})`;
  }

  if (format === "hex") {
    return rgbToHex(value);
  } else {
    return value;
  }
};
